import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send } from 'lucide-react';
import { ConversationPane } from '../ConversationPane';
import { VerboseTimeline } from '../../../../components/VerboseTimeline';
import type { BuilderMessage } from '@/lib/api';
import type { VerboseEvent } from '../../hooks/useBuilderSession';
import type { GuidedPhase } from '../../types/guided-builder';

interface WizardStepWithChatProps {
  /** The guided panel content (ClarificationControls, PlanReview, AssumptionReview) */
  children: React.ReactNode;
  /** Chat messages to display in the inline chat area */
  messages: BuilderMessage[];
  /** Whether AI is currently processing */
  isProcessing: boolean;
  /** Streaming text for the conversation */
  streamingText?: Record<string, string>;
  /** Callback to send a chat message */
  onSendMessage: (message: string) => void;
  /** Current guided phase — used to auto-close chat on phase advance */
  guidedPhase: GuidedPhase;
  /** Whether to force the chat open (e.g. when "Request Changes" is clicked) */
  chatOpen?: boolean;
  /** Callback when chat visibility changes */
  onChatOpenChange?: (open: boolean) => void;
  /** Whether verbose mode is on */
  verbose?: boolean;
  /** Committed verbose events keyed by assistant message timestamp */
  verboseHistory?: Record<string, VerboseEvent[]>;
  /** In-progress verbose events for the current response */
  pendingVerboseEvents?: VerboseEvent[];
}

export function WizardStepWithChat({
  children,
  messages,
  isProcessing,
  streamingText,
  onSendMessage,
  guidedPhase,
  chatOpen: chatOpenProp,
  onChatOpenChange,
  verbose,
  verboseHistory,
  pendingVerboseEvents,
}: WizardStepWithChatProps) {
  const [internalChatOpen, setInternalChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Use controlled or uncontrolled chat open state
  const chatOpen = chatOpenProp !== undefined ? chatOpenProp : internalChatOpen;
  const setChatOpen = (open: boolean) => {
    setInternalChatOpen(open);
    onChatOpenChange?.(open);
  };

  // Auto-close chat when phase advances
  const prevPhaseRef = useRef(guidedPhase);
  useEffect(() => {
    if (guidedPhase !== prevPhaseRef.current) {
      prevPhaseRef.current = guidedPhase;
      setChatOpen(false);
    }
  }, [guidedPhase]); // eslint-disable-line

  // Focus chat input when chat opens
  useEffect(() => {
    if (chatOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [chatOpen]);

  const handleSend = () => {
    const trimmed = chatInput.trim();
    if (!trimmed || isProcessing) return;
    setChatInput('');
    onSendMessage(trimmed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const hasPendingVerbose = verbose && pendingVerboseEvents && pendingVerboseEvents.length > 0;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Panel content */}
      <div
        className={`${chatOpen || hasPendingVerbose ? 'flex-[3]' : 'flex-1'} overflow-y-auto min-h-0`}
      >
        {children}
      </div>

      {/* Pending verbose timeline — shown between panel and chat toggle for real-time visibility */}
      {hasPendingVerbose && !chatOpen && (
        <div className="border-t overflow-y-auto max-h-[40%] min-h-0 flex-[2] px-4 py-2">
          <VerboseTimeline events={pendingVerboseEvents} isPending />
        </div>
      )}

      {/* Chat toggle button */}
      <button
        type="button"
        onClick={() => setChatOpen(!chatOpen)}
        className="flex items-center justify-center gap-1.5 px-3 py-1.5 border-t border-b text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors flex-shrink-0"
      >
        <MessageCircle className="h-3.5 w-3.5" />
        Chat with AI
        {chatOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
      </button>

      {/* Inline chat area */}
      {chatOpen && (
        <div className="flex-[2] flex flex-col min-h-0">
          <ConversationPane
            messages={messages}
            isProcessing={isProcessing}
            streamingText={streamingText}
            verboseHistory={verbose ? verboseHistory : undefined}
            pendingVerboseEvents={verbose ? pendingVerboseEvents : undefined}
            className="flex-1 min-h-0"
          />
          <div className="border-t p-2 flex gap-2 flex-shrink-0">
            <Textarea
              ref={textareaRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={isProcessing}
              className="min-h-[40px] max-h-[80px] resize-none text-sm"
            />
            <Button
              onClick={handleSend}
              disabled={!chatInput.trim() || isProcessing}
              size="icon"
              className="flex-shrink-0 h-10 w-10"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
